import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation} from "react-router-dom";
import API_URL from "../../src/Config";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const MenuItem = ({ item, level, onToggle, expanded, checked, onCheck }) => {
  const hasSubItems = item.sub_menu && item.sub_menu.length > 0;
  const isExpanded = expanded[item.id];
  

  return (
    <>
      <tr className="table-row">
        <td className="table-cell" style={{ paddingLeft: `${level * 20}px` }}>
          <div className="menu-item">
            <input
              type="checkbox"
              id={item.id}
              checked={checked[item.id] || false}
              onChange={(e) => {
                console.log(item.id, "checking");
                onCheck(item.id, e.target.checked, item);
              }}
              className="checkbox"
            />
            {hasSubItems && (
              <button onClick={() => onToggle(item.id)} className="toggle-btn">
                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
              </button>
            )}
            <label htmlFor={item.id} className="menu-label">
              {item.name}
            </label>
          </div>
        </td>
      </tr>
      {hasSubItems &&
        isExpanded &&
        item.sub_menu.map((subItem) => (
          <MenuItem
            key={subItem.id}
            item={subItem}
            level={level + 1}
            onToggle={onToggle}
            expanded={expanded}
            checked={checked}
            onCheck={onCheck}
          />
        ))}
    </>
  );
};

function AdminUserAccessTable({ isSidebarExpanded }) {
  const [allMenuItems, setAllMenuItems] = useState([]); // All available menu items
  const [accessedMenuItems, setAccessedMenuItems] = useState([]); // Currently accessed menu items
  const [projectTypeId, setProjectTypeId] = useState(null); // Store project_type_id
  const [expanded, setExpanded] = useState({});
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severity, setSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { ID } = useParams(); // Company ID
  const navigate = useNavigate();
  const location = useLocation();
  const project_type_id = location.state?.project_type_id;

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch all available menu items
  const fetchAllMenuItems = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/user_access/get_menu_items`,
        {
          headers: { Authorization: authKey },
        }
      );
      setAllMenuItems(response.data);
      console.log("All menu items fetched:", response.data);
    } catch (error) {
      console.error("Error fetching all menu items:", error.message);
      setError(error.message);
    }
  };

  // Fetch currently accessed menu items for admin
  const fetchAccessedMenuItems = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/get_admin_accessed_menu_items?company_id=${ID}`,
        {
          headers: { Authorization: authKey },
        }
      );
      
      setAccessedMenuItems(response.data);
      console.log("Accessed menu items:", response.data);

      // Pre-check items based on currently accessed menu items
      const preChecked = {};
      const setCheckedRecursively = (items) => {
        items.forEach((item) => {
          preChecked[item.id] = true;
          if (item.sub_menu && item.sub_menu.length > 0) {
            setCheckedRecursively(item.sub_menu);
          }
        });
      };
      
      setCheckedRecursively(response.data);
      setChecked(preChecked);
      
    } catch (error) {
      console.error("Error fetching accessed menu items:", error.message);
      // If no accessed items found, it's not necessarily an error
      if (error.response?.status !== 404) {
        setError(error.message);
      }
    }
  };


  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchAllMenuItems(),
          fetchAccessedMenuItems(),
          // getProjectTypeId() // Uncomment if you have this endpoint
        ]);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (ID) {
      loadData();
    }
  }, [ID]);

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCheck = (id, isChecked, item) => {
    setChecked((prev) => {
      const updatedChecked = { ...prev, [id]: isChecked };

      // If the item has submenus, update the checked state of all submenus
      const updateSubMenus = (menuItem, checked) => {
        if (menuItem.sub_menu && menuItem.sub_menu.length > 0) {
          menuItem.sub_menu.forEach((subItem) => {
            updatedChecked[subItem.id] = checked;
            updateSubMenus(subItem, checked); // Handle nested submenus
          });
        }
      };

      updateSubMenus(item, isChecked);
      return updatedChecked;
    });
  };

  const handleSave = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const selectedMenuItemIds = Object.keys(checked).filter((key) => checked[key]);

      const updateData = {
        project_type_id: project_type_id,
        company_id: ID,
        menu_item_ids: selectedMenuItemIds
      };

      await axios.post(
        `${API_URL}/api/update_admin_project_menu_items`,
        updateData,
        { 
          headers: { Authorization: authKey },
        }
      );

      showSnackbar("Admin menu access updated successfully", "success");
      setTimeout(() => {
        navigate("/admin-users"); // or wherever you want to navigate back
      }, 1500);
      
    } catch (error) {
      console.error("Error updating admin menu access:", error.message);
      const errorMessage = error.response?.data?.error || "Failed to update menu access";
      showSnackbar(errorMessage, "error");
    }
  };

  if (loading) return <div className="loader-container">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const handleCancelClick = () => {
    navigate("/admin-users");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-header">
          <div className="data-list-header-content">
            <PageBackTitle>Admin Menu Access Configuration</PageBackTitle>
          </div>
        </div>

        <div className="data-list-body">
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              <table className="table multilevel-data">
                <thead>
                  <tr>
                    <th className="table-header">Menu Items</th>
                  </tr>
                </thead>
                <tbody className="data-list-scroll-bodys">
                  {allMenuItems?.length > 0 &&
                    allMenuItems.map((item) => (
                      <MenuItem
                        key={item.id}
                        item={item}
                        level={0}
                        onToggle={handleToggle}
                        expanded={expanded}
                        checked={checked}
                        onCheck={handleCheck}
                      />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="footer">
          <div className="button-group">
            <button
              className="data-form-save-button"
              type="button"
              onClick={handleSave}
              disabled={loading}
            >
              Save Menu Access
            </button>
            <button
              className="data-form-cancel-button"
              type="button"
              onClick={handleCancelClick}
            >
              Cancel
            </button>
          </div>
        </div>

<Snackbar
        open={snackbarOpen}
        autoHideDuration={6000} // Converted to milliseconds
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      </div>
    </>
  );
}

export default AdminUserAccessTable;